import L from "leaflet";
import { MapPin, Loader2, AlertCircle } from "lucide-react";
import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";

import { HeatMapLegend } from "./HeatMapLegend";
import { MapTimeFilters } from "./MapTimeFilters";

import { useMapView } from "@/hooks/useMapView";
import type { ProcessedEvent, GeocodedEvent } from "@/types/calendar";
import {
  formatCountdownForContext,
  shouldPulse,
} from "@/utils/countdown.utils";
import { getMarkerColor, getMarkerBorderColor } from "@/utils/map.utils";
import "leaflet/dist/leaflet.css";

// Fix for default marker icons in Leaflet with Vite
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface CalendarMapViewProps {
  events: ProcessedEvent[];
  onEventClick?: (event: ProcessedEvent) => void;
}

// Component to capture map reference
const MapRefSetter: React.FC<{
  mapRef: React.MutableRefObject<L.Map | null>;
}> = ({ mapRef }) => {
  const map = useMap();

  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);

  return null;
};

// Component to handle map bounds updates - only on initial load
const MapBoundsUpdater: React.FC<{
  bounds: L.LatLngBounds | null;
  shouldUpdate: boolean;
}> = ({ bounds, shouldUpdate }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds && shouldUpdate) {
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 15,
      });
    }
  }, [bounds, map, shouldUpdate]);

  return null;
};

export const CalendarMapView: React.FC<CalendarMapViewProps> = ({
  events,
  onEventClick,
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const {
    filteredEvents,
    geocodedEvents,
    groupedEvents,
    timeFilter,
    setTimeFilter,
    selectedEvent,
    setSelectedEvent,
    isGeocoding,
    geocodingProgress,
    mapBounds,
    eventsWithLocation,
  } = useMapView(events);

  // Default center (San Francisco)
  const [mapCenter] = useState<[number, number]>([37.7749, -122.4194]);
  const [mapZoom] = useState(12);
  const [hasSetInitialBounds, setHasSetInitialBounds] = useState(false);
  const [shouldFitBounds, setShouldFitBounds] = useState(false);

  // Create custom icon with event color and FULL countdown timer
  const createCustomIcon = (event: GeocodedEvent, eventCount = 1) => {
    const color = getMarkerColor(event);
    const borderColor = getMarkerBorderColor(event);
    const countdown = event.countdownData;
    const countdownText = countdown
      ? formatCountdownForContext(countdown, "marker")
      : "";
    const pulse = countdown && shouldPulse(countdown.urgencyLevel);

    // Always show timer (even for multiple events, show the closest one's timer)
    return L.divIcon({
      html: `
        <div class="map-marker-container" style="position: relative;">
          <!-- Timer Display Card -->
          <div class="timer-card ${pulse ? "timer-pulse" : ""}" style="
            background: ${color};
            color: white;
            padding: 6px 12px;
            border-radius: 8px;
            border: 2px solid ${borderColor};
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            font-family: 'Courier New', monospace;
            font-size: 13px;
            font-weight: 700;
            text-align: center;
            white-space: nowrap;
            position: relative;
            z-index: 1000;
            min-width: fit-content;
            display: inline-block;
          ">
            <div style="text-shadow: 0 1px 3px rgba(0,0,0,0.5);">
              ${countdownText || "•"}
            </div>
            ${
              eventCount > 1
                ? `
              <div style="
                position: absolute;
                top: -8px;
                right: -8px;
                background: white;
                color: ${color};
                border: 2px solid ${borderColor};
                border-radius: 50%;
                width: 20px;
                height: 20px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 11px;
                font-weight: bold;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
              ">
                ${eventCount}
              </div>
            `
                : ""
            }
          </div>

          <!-- Pointer arrow -->
          <div style="
            width: 0;
            height: 0;
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-top: 8px solid ${color};
            position: absolute;
            bottom: -8px;
            left: 50%;
            transform: translateX(-50%);
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
          "></div>
        </div>

        <style>
          .timer-pulse {
            animation: timerPulse 2s infinite;
          }
          @keyframes timerPulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.9;
              transform: scale(1.05);
            }
          }
        </style>
      `,
      className: "custom-timer-marker",
      iconSize: [120, 40],
      iconAnchor: [60, 48],
      popupAnchor: [0, -48],
    });
  };

  // Set initial bounds only once when events are loaded
  useEffect(() => {
    if (
      !hasSetInitialBounds &&
      mapBounds &&
      geocodedEvents.length > 0 &&
      !isGeocoding
    ) {
      setHasSetInitialBounds(true);
      setShouldFitBounds(true);
    }
  }, [mapBounds, geocodedEvents, isGeocoding, hasSetInitialBounds]);

  // Re-fit bounds when time filter changes
  useEffect(() => {
    if (hasSetInitialBounds && !isGeocoding && geocodedEvents.length > 0) {
      setShouldFitBounds(true);
      // Reset after a short delay to allow the MapBoundsUpdater to process
      const timer = setTimeout(() => setShouldFitBounds(false), 100);
      return () => clearTimeout(timer);
    }
  }, [timeFilter, hasSetInitialBounds, isGeocoding, geocodedEvents.length]);

  // Function to center map on a specific event
  const centerOnEvent = (event: GeocodedEvent) => {
    if (event.coordinates && mapRef.current) {
      mapRef.current.setView(event.coordinates, 15, {
        animate: true,
        duration: 0.5,
      });
    }
  };

  const handleMarkerClick = (eventGroup: GeocodedEvent[]) => {
    // Always select the closest/soonest event and open sidebar
    const target = eventGroup[0];
    if (onEventClick) {
      onEventClick(target);
    }
    setSelectedEvent(target);
    centerOnEvent(target);

    // Close any open Leaflet popups so they don't float over the sidebar
    if (mapRef.current) {
      mapRef.current.closePopup();
    }
  };

  // Handle event click from sidebar list
  const handleEventListClick = (event: ProcessedEvent) => {
    const geocodedEvent = geocodedEvents.find((e) => e.id === event.id);
    if (geocodedEvent?.coordinates) {
      if (onEventClick) {
        onEventClick(event);
      }
      setSelectedEvent(event);
      centerOnEvent(geocodedEvent);
    }
  };

  // Show loading state
  if (isGeocoding && geocodedEvents.length === 0) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 text-white">
          <h2 className="text-xl font-bold">Map View</h2>
          <p className="text-sm opacity-90 mt-1">Visualize event locations</p>
        </div>
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-3" />
            <p className="text-gray-600">Locating events on map...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show no events message
  if (eventsWithLocation.length === 0) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 text-white">
          <h2 className="text-xl font-bold">Map View</h2>
          <p className="text-sm opacity-90 mt-1">Visualize event locations</p>
        </div>
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">
              No events with physical locations
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Events with addresses will appear on the map
            </p>
          </div>
        </div>
      </div>
    );
  }

  const successfullyGeocoded = geocodedEvents.filter((e) => e.coordinates);
  const failedGeocoding = geocodedEvents.filter((e) => !e.coordinates);

  return (
    <div className="h-full flex flex-col bg-gray-100 isolate">
      {/* Header */}
      <div
        className="p-4 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 text-white shadow-lg"
        role="banner"
      >
        <div className="flex flex-col gap-3">
          {/* Title and Count */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold" id="map-view-title">
              Map View
            </h2>
            {isGeocoding ? (
              <p
                className="text-sm opacity-90"
                aria-live="polite"
                aria-atomic="true"
              >
                Locating events... {geocodingProgress.current} of{" "}
                {geocodingProgress.total}
              </p>
            ) : (
              <p
                className="text-sm opacity-90"
                aria-live="polite"
                aria-atomic="true"
              >
                Showing {successfullyGeocoded.length} of {filteredEvents.length}{" "}
                events
              </p>
            )}
          </div>

          {/* Progress Bar or Heat Map Legend */}
          {isGeocoding ? (
            <div
              className="w-full bg-indigo-800 rounded-full h-2"
              role="progressbar"
              aria-valuenow={
                geocodingProgress.total > 0
                  ? Math.round(
                      (geocodingProgress.current / geocodingProgress.total) *
                        100,
                    )
                  : 0
              }
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Geocoding progress"
            >
              <div
                className="bg-white h-2 rounded-full transition-all duration-300"
                style={{
                  width:
                    geocodingProgress.total > 0
                      ? `${(geocodingProgress.current / geocodingProgress.total) * 100}%`
                      : "0%",
                }}
              />
            </div>
          ) : successfullyGeocoded.length > 0 ? (
            <HeatMapLegend />
          ) : null}
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Left sidebar with filters - Responsive */}
        <aside
          className="w-full lg:w-80 bg-white shadow-lg z-10 p-4 overflow-y-auto lg:max-h-full max-h-48 sm:max-h-64"
          role="complementary"
          aria-label="Event filters and list"
        >
          <MapTimeFilters
            timeFilter={timeFilter}
            onFilterChange={setTimeFilter}
            eventCount={
              isGeocoding ? geocodingProgress.current : filteredEvents.length
            }
            isLoading={isGeocoding}
          />

          {/* Event list */}
          <nav className="mt-4" aria-label="Events list">
            <h3
              className="text-sm font-semibold text-gray-900 mb-3"
              id="events-list-heading"
            >
              Events
            </h3>
            <ul
              className="space-y-2"
              role="list"
              aria-labelledby="events-list-heading"
            >
              {filteredEvents.map((event) => {
                const isSelected = selectedEvent?.id === event.id;
                const hasCoords = geocodedEvents.find(
                  (e) => e.id === event.id,
                )?.coordinates;

                return (
                  <li key={event.id}>
                    <button
                      onClick={() => {
                        if (hasCoords) {
                          handleEventListClick(event);
                        }
                      }}
                      className={`
                        w-full text-left p-3 rounded-lg transition-all
                        min-h-[44px] touch-manipulation
                        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                        ${
                          isSelected
                            ? "bg-indigo-50 border-2 border-indigo-500"
                            : "bg-white border border-gray-200 hover:border-indigo-300 hover:shadow-sm"
                        }
                        ${!hasCoords ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                      `}
                      disabled={!hasCoords}
                      aria-pressed={isSelected}
                      aria-label={`${event.title} at ${event.location}${!hasCoords ? " - Location unavailable" : ""}`}
                      aria-disabled={!hasCoords}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 flex-wrap">
                            <h4 className="text-sm sm:text-base font-medium text-gray-900 truncate">
                              {event.title}
                            </h4>
                            {event.isRecurring && (
                              <span
                                className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded flex-shrink-0"
                                aria-label="Recurring event"
                              >
                                🔁
                              </span>
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1">
                            {(
                              event.nextOccurrence || event.start
                            ).toLocaleTimeString("en-US", {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </p>
                          {event.countdownData &&
                            !event.countdownData.isPast && (
                              <p
                                className="text-xs sm:text-sm font-semibold mt-1"
                                style={{
                                  color:
                                    event.countdownData.urgencyLevel ===
                                    "imminent"
                                      ? "#ef4444"
                                      : event.countdownData.urgencyLevel ===
                                          "now"
                                        ? "#22c55e"
                                        : "#6366f1",
                                }}
                                aria-label={`Starts in ${event.countdownData.formattedCountdown}`}
                              >
                                {event.countdownData.formattedCountdown}
                              </p>
                            )}
                          <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-1">
                            {event.location}
                          </p>
                        </div>
                        {!hasCoords && (
                          <AlertCircle
                            className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500 ml-2 flex-shrink-0"
                            aria-label="Location unavailable"
                          />
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {failedGeocoding.length > 0 && (
            <div
              className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg"
              role="alert"
              aria-live="polite"
            >
              <p className="text-xs sm:text-sm text-amber-800 font-medium">
                {failedGeocoding.length} event
                {failedGeocoding.length !== 1 ? "s" : ""} couldn't be located on
                the map
              </p>
            </div>
          )}
        </aside>

        {/* Map */}
        <main
          className="flex-1 relative min-h-[60vh] lg:min-h-0"
          role="main"
          aria-labelledby="map-view-title"
        >
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            className="h-full w-full"
            style={{ height: "100%", width: "100%" }}
            aria-label="Interactive map showing event locations"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {/* Capture map reference */}
            <MapRefSetter mapRef={mapRef} />

            {/* Add bounds updater - updates on initial load and when filter changes */}
            {mapBounds && (
              <MapBoundsUpdater
                bounds={mapBounds}
                shouldUpdate={
                  shouldFitBounds && !isGeocoding && geocodedEvents.length > 0
                }
              />
            )}

            {/* Render markers for each unique location */}
            {Array.from(groupedEvents.entries()).map(
              ([location, eventGroup]) => {
                // Find the closest/soonest event in this group
                const closestEvent = eventGroup.reduce((closest, event) => {
                  if (!event.countdownData || !closest.countdownData)
                    return event;

                  // If event is happening now, prioritize it
                  if (event.countdownData.isHappening) return event;
                  if (closest.countdownData.isHappening) return closest;

                  // Otherwise, use the one with less time remaining (closest to starting)
                  return event.countdownData.timeRemaining <
                    closest.countdownData.timeRemaining
                    ? event
                    : closest;
                }, eventGroup[0]);

                if (!closestEvent.coordinates) return null;

                return (
                  <Marker
                    key={location}
                    position={closestEvent.coordinates}
                    icon={createCustomIcon(closestEvent, eventGroup.length)}
                    eventHandlers={{
                      click: () => handleMarkerClick(eventGroup),
                    }}
                    aria-label={`${eventGroup.length} event${eventGroup.length > 1 ? "s" : ""} at ${closestEvent.location}`}
                  />
                );
              },
            )}
          </MapContainer>

          {/* Mobile-friendly map instructions */}
          <div className="lg:hidden absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg text-xs text-gray-700 max-w-[90%] text-center">
            <p>Pinch to zoom • Drag to pan</p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CalendarMapView;
